"use client";

import { type ButtonHTMLAttributes, type ReactNode, useState } from "react";
import { E_OUT } from "@/lib/motion";

type Variant = "primary" | "ghost" | "destructive" | "link" | "gold" | "buy";
type Size = "default" | "small";

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  children: ReactNode;
  full?: boolean;
};

/* Brand CTA with fill-from-below hover animation.
 *
 * Rest → hover swap:
 *   primary       (blue → white)        bg fills white, text → black
 *   ghost         (white → blue)        bg fills blue,  text → white
 *   destructive   (red → white)         bg fills white, text → black
 *   link          (no fill)             text → accent
 *
 * The hover layer is an absolutely-positioned span that slides up from the
 * bottom (scale-y 0 → 1, transform-origin: bottom). Text colour eases
 * in parallel. 500ms fill, 300ms text, ease-out. */

const COLORS: Record<
  Variant,
  { restBg: string; restFg: string; hoverBg: string; hoverFg: string }
> = {
  /* Text colors are contrast-checked against their own background, not
   * inherited from a "looks right" guess — accent, magenta and the old
   * destructive red all measured under WCAG AA's 4.5:1 floor with white
   * text (2.7:1 / 3.3:1 / 3.8:1). Dark text on accent/magenta, and a
   * darker red under white text, all clear AA (2026-09-07 fine-tune). */
  primary: {
    restBg: "linear-gradient(135deg,#7a4a22,#c98b4a 24%,#f6dcab 42%,#b87333 62%,#e6b36a)",
    restFg: "#1a0f06",
    hoverBg: "#f6dcab",
    hoverFg: "#0A0A0A",
  },
  ghost: {
    restBg: "#1c1610",
    restFg: "#fff6e6",
    hoverBg: "linear-gradient(135deg,#7a4a22,#c98b4a 24%,#f6dcab 42%,#b87333 62%,#e6b36a)",
    hoverFg: "#1a0f06",
  },
  /* Hot Magenta — buy / checkout buttons only (Website Domination SOP
   * §3.1: magenta = "buy buttons, sale badges, urgency"). Pops hard
   * against the onyx + sapphire so the purchase action is unmistakable. */
  buy: {
    restBg: "linear-gradient(135deg,#7a4a22,#c98b4a 24%,#f6dcab 42%,#b87333 62%,#e6b36a)",
    restFg: "#1a0f06",
    hoverBg: "#f6dcab",
    hoverFg: "#0A0A0A",
  },
  destructive: {
    restBg: "#DC2626",
    restFg: "#FFFFFF",
    hoverBg: "#FFFFFF",
    hoverFg: "#0A0A0A",
  },
  link: {
    restBg: "transparent",
    restFg: "#FFFFFF",
    hoverBg: "transparent",
    hoverFg: "var(--color-accent)",
  },
  /* Apex Gold — reserved for premium / Insider Pass surfaces. Used sparingly
   * (5-10% of any screen). Rest is dark with gold border + gold text;
   * hover fills with gold + dark text inverse. */
  gold: {
    restBg: "transparent",
    restFg: "var(--color-brand-flow)",
    hoverBg: "var(--color-brand-flow)",
    hoverFg: "#0A0A0A",
  },
};

const SIZES: Record<Size, { padding: string; fontSize: number }> = {
  default: { padding: "16px 26px", fontSize: 15 },
  small: { padding: "10px 18px", fontSize: 13 },
};

export function Cta({
  variant = "primary",
  size = "default",
  children,
  full,
  className = "",
  style,
  ...rest
}: Props) {
  const [hov, setHov] = useState(false);
  const c = COLORS[variant];
  const s = SIZES[size];

  return (
    <button
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      data-interactive
      className={`relative inline-flex items-center justify-center overflow-hidden rounded-lg font-medium select-none ${full ? "w-full" : ""} ${className}`}
      style={{
        padding: variant === "link" ? "0" : s.padding,
        fontSize: s.fontSize,
        background: c.restBg,
        color: hov ? c.hoverFg : c.restFg,
        border:
          variant === "gold"
            ? "1px solid var(--color-brand-flow)"
            : variant === "ghost"
              ? "1px solid #7a4a22"
              : "none",
        cursor: "inherit",
        transition: `color 280ms ${E_OUT}`,
        ...style,
      }}
      {...rest}
    >
      {variant !== "link" && (
        <span
          aria-hidden
          className="absolute inset-0"
          style={{
            background: c.hoverBg,
            transform: hov ? "scaleY(1)" : "scaleY(0)",
            transformOrigin: "bottom",
            transition: `transform 500ms ${E_OUT}`,
          }}
        />
      )}
      <span className="relative">{children}</span>
    </button>
  );
}
